import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { toast } from "sonner";
import { apiClient } from "../../lib/api";

// *** Updated UploadedFile interface ***
export interface UploadedFile {
  sessionId: string;
  name: string;
  sourceType: string; // 'file_upload', 'live_device', 'virtual_debug'
  sourceDeviceId?: string | null; // Device ID if assigned
  startTimestamp: number;
  endTimestamp: number;
  duration: number;
  size: number;
  description: string;
  /** The user's key:value tags on the recording. */
  tags: Record<string, string>;
  /** The source device's tags when the recording started, if known. */
  deviceTags?: Record<string, string>;
  createdAt: string; // Upload timestamp
  deviceName?: string; // Optional: Name of the device (denormalized for display)
  s3ObjectKey?: string | null; // Optional: S3 object key for MCAP file viewing
  // Add other relevant fields if needed, e.g., upload_method: 'manual' | 'device_sync'
}

interface FileState {
  files: UploadedFile[];
  loading: boolean;
  error: string | null;
  deletingStatuses: Record<string, string>;
}

const initialState: FileState = {
  files: [],
  loading: false,
  error: null,
  deletingStatuses: {},
};

// Thunk to fetch uploaded log files
export const fetchFiles = createAsyncThunk(
  "files/fetchFiles",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<{ files: UploadedFile[] }>(
        "/logs/uploaded",
      );
      return response.data.files || response.data; // Adjust based on actual API response
    } catch (error: any) {
      console.error("Error fetching files:", error);
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch log files";
      return rejectWithValue(errorMsg);
    }
  },
);

// Thunk to delete a log file
export const deleteFile = createAsyncThunk(
  "files/deleteFile",
  async ({ id, name }: { id: string; name: string }, { rejectWithValue }) => {
    try {
      const response = await apiClient.delete<{
        sessionId: string;
        status: string;
      }>(`/logs/${id}`);
      return {
        id,
        name,
        status: response.data.status || "PENDING",
      };
    } catch (error: any) {
      console.error("Error deleting file:", error);
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Failed to delete log file";
      // Pass necessary info for potential error display using name
      return rejectWithValue({ errorMsg, name });
    }
  },
);

export const fetchDeleteFileStatus = createAsyncThunk(
  "files/fetchDeleteFileStatus",
  async ({ id }: { id: string }, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<{
        sessionId: string;
        status: string;
      }>(`/logs/sessions/${id}/delete-status`);
      return {
        id,
        status: response.data.status || "UNKNOWN",
      };
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch delete status";
      return rejectWithValue({ id, errorMsg });
    }
  },
);

const fileSlice = createSlice({
  name: "files",
  initialState,
  reducers: {
    // Optional: Add reducers for optimistic updates if needed
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFiles.pending, (state) => {
        state.loading = true;
        state.error = null; // Clear previous errors
      })
      .addCase(
        fetchFiles.fulfilled,
        (state, action: PayloadAction<UploadedFile[]>) => {
          state.loading = false;
          // Sort files by upload date descending by default. Copy first:
          // `sort` is in-place, and the payload must not be mutated.
          state.files = [...action.payload].sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          );
        },
      )
      .addCase(fetchFiles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteFile.fulfilled, (state, action) => {
        state.deletingStatuses[action.payload.id] = action.payload.status;
        toast.info(action.payload.name, {
          description: "Deletion started. This can take a few moments.",
        });
      })
      .addCase(deleteFile.rejected, (state, action) => {
        // Extract error message and name from payload
        const { errorMsg, name } = action.payload as {
          errorMsg: string;
          name?: string;
        };
        toast.error(name || "Delete Error", {
          description: errorMsg,
        });
        state.error = errorMsg; // Store only the error message
      })
      .addCase(fetchDeleteFileStatus.fulfilled, (state, action) => {
        const { id, status } = action.payload;
        state.deletingStatuses[id] = status;
        if (status === "COMPLETED") {
          const deletedFile = state.files.find((file) => file.sessionId === id);
          state.files = state.files.filter((file) => file.sessionId !== id);
          delete state.deletingStatuses[id];
          toast.success(deletedFile?.name || "Log", {
            description: "Log deleted successfully.",
          });
        }
        if (status === "FAILED") {
          delete state.deletingStatuses[id];
          toast.error("Delete Error", {
            description: "Failed to delete log. Please try again.",
          });
        }
      })
      .addCase(fetchDeleteFileStatus.rejected, (state, action) => {
        const payload = action.payload as { id?: string; errorMsg?: string };
        if (payload?.id) {
          delete state.deletingStatuses[payload.id];
        }
        if (payload?.errorMsg) {
          state.error = payload.errorMsg;
        }
      });
  },
});

// Export reducer
export const fileReducer = fileSlice.reducer;

// Export Selectors
export const selectAllFiles = (state: {
  fileReducer: FileState;
}): UploadedFile[] => state.fileReducer.files;
export const selectFilesLoading = (state: {
  fileReducer: FileState;
}): boolean => state.fileReducer.loading;
export const selectFilesError = (state: {
  fileReducer: FileState;
}): string | null => state.fileReducer.error;

export const selectDeletingStatuses = (state: {
  fileReducer: FileState;
}): Record<string, string> => state.fileReducer.deletingStatuses;
